/**
 * Bestellungsregeln - Komplettes Konfigurationssystem
 * Alle 3 Phasen: MUST-HAVE, SHOULD-HAVE, NICE-TO-HAVE
 * 
 * Übertrifft alle Konkurrenten: Lightspeed, Square, Toast
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  MapPin, Clock, Euro, ShieldAlert, TrendingUp, Plus, Trash2, Edit, 
  CheckCircle, XCircle, AlertCircle, Timer, Package, Truck, Coffee
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function OrderRulesSettings() {
  const [activeTab, setActiveTab] = useState('order-types');
  const [loading, setLoading] = useState(true);

  // State
  const [orderTypeRules, setOrderTypeRules] = useState({});
  const [deliveryZones, setDeliveryZones] = useState([]);
  const [availabilityRules, setAvailabilityRules] = useState([]);
  const [blacklist, setBlacklist] = useState([]);
  const [stats, setStats] = useState({});

  // Form states
  const [editingZone, setEditingZone] = useState(null);
  const [newZone, setNewZone] = useState({
    name: '',
    postal_codes: '',
    min_order_value: 15,
    delivery_fee: 3.5,
    delivery_fee_free_above: 30,
    estimated_time_min: 30,
    estimated_time_max: 45
  });

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [typesRes, zonesRes, rulesRes, statsRes] = await Promise.all([
        fetch(`${API_BASE}/api/order-rules/order-types`, { headers }),
        fetch(`${API_BASE}/api/order-rules/delivery-zones`, { headers }),
        fetch(`${API_BASE}/api/order-rules/product-availability-rules`, { headers }),
        fetch(`${API_BASE}/api/order-rules/stats`, { headers })
      ]);

      if (typesRes.ok) {
        const data = await typesRes.json();
        const rulesMap = {};
        data.rules.forEach(r => {
          rulesMap[r.order_type] = r;
        });
        setOrderTypeRules(rulesMap);
      }

      if (zonesRes.ok) {
        const data = await zonesRes.json();
        setDeliveryZones(data.zones);
      }

      if (rulesRes.ok) {
        const data = await rulesRes.json();
        setAvailabilityRules(data.rules);
      }

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }

    } catch (error) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const updateOrderTypeRule = async (orderType, updates) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE}/api/order-rules/order-types/${orderType}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          order_type: orderType,
          ...updates
        })
      });

      if (response.ok) {
        toast.success('Gespeichert!');
        loadAllData();
      }
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const createDeliveryZone = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE}/api/order-rules/delivery-zones`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...newZone,
          postal_codes: newZone.postal_codes.split(',').map(p => p.trim())
        })
      });

      if (response.ok) {
        toast.success('Liefergebiet erstellt!');
        setNewZone({
          name: '',
          postal_codes: '',
          min_order_value: 15,
          delivery_fee: 3.5,
          delivery_fee_free_above: 30,
          estimated_time_min: 30,
          estimated_time_max: 45
        });
        loadAllData();
      }
    } catch (error) {
      toast.error('Fehler');
    }
  };

  const deleteDeliveryZone = async (zoneId) => {
    if (!confirm('Liefergebiet wirklich löschen?')) return;

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE}/api/order-rules/delivery-zones/${zoneId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Gelöscht');
        loadAllData();
      }
    } catch (error) {
      toast.error('Fehler');
    }
  };

  const pauseAllOrders = async (paused) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE}/api/order-rules/pause-orders`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          paused: paused,
          reason: paused ? 'Manuell pausiert' : ''
        })
      });

      if (response.ok) {
        toast.success(paused ? 'Bestellungen pausiert' : 'Bestellungen fortgesetzt');
        loadAllData();
      }
    } catch (error) {
      toast.error('Fehler');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-[#0B1220] to-[#0E1628]">
        <div className="text-white">Lädt Bestellungsregeln...</div>
      </div>
    );
  }

  const orderTypes = [
    { key: 'pickup', label: 'Abholung', icon: Package, color: 'blue' },
    { key: 'delivery', label: 'Lieferung', icon: Truck, color: 'emerald' },
    { key: 'dine_in', label: 'Tisch', icon: Coffee, color: 'amber' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B1220] to-[#0E1628] p-6">
      <div className="max-w-7xl mx-auto">
        
        {/* Header mit Stats */}
        <div className="mb-6">
          <h1 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Bestellungsregeln
          </h1>
          <p className="text-slate-400 mb-4">
            Vollständige Kontrolle über Bestellannahme, Liefergebiete und Verfügbarkeit
          </p>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-4 gap-3">
            <Card className="bg-[#111C32]/80 border-slate-700/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-teal-500/20 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-teal-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">{stats.delivery_zones_active || 0}</div>
                    <div className="text-xs text-slate-400">Liefergebiete</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#111C32]/80 border-slate-700/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">{stats.availability_rules_active || 0}</div>
                    <div className="text-xs text-slate-400">Zeitregeln</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#111C32]/80 border-slate-700/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                    <ShieldAlert className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">{stats.blacklist_entries || 0}</div>
                    <div className="text-xs text-slate-400">Gesperrt</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#111C32]/80 border-slate-700/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                    <XCircle className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">{stats.rejected_orders_today || 0}</div>
                    <div className="text-xs text-slate-400">Abgelehnt heute</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-[#111C32] border border-slate-700/50 p-1 flex-wrap h-auto">
            <TabsTrigger value="order-types" className="data-[state=active]:bg-teal-500 data-[state=active]:text-white">
              <Package className="w-4 h-4 mr-2" />
              Bestelltypen
            </TabsTrigger>
            <TabsTrigger value="delivery-zones" className="data-[state=active]:bg-teal-500 data-[state=active]:text-white">
              <MapPin className="w-4 h-4 mr-2" />
              Liefergebiete
            </TabsTrigger>
            <TabsTrigger value="availability" className="data-[state=active]:bg-teal-500 data-[state=active]:text-white">
              <Clock className="w-4 h-4 mr-2" />
              Produktzeiten
            </TabsTrigger>
            <TabsTrigger value="advanced" className="data-[state=active]:bg-teal-500 data-[state=active]:text-white">
              <TrendingUp className="w-4 h-4 mr-2" />
              Erweitert
            </TabsTrigger>
          </TabsList>

          {/* TAB 1: BESTELLTYPEN */}
          <TabsContent value="order-types">
            <div className="grid grid-cols-1 gap-4">
              {orderTypes.map(({ key, label, icon: Icon, color }) => {
                const rule = orderTypeRules[key] || {
                  enabled: true,
                  min_order_value: 0,
                  auto_accept: true,
                  lead_time_minutes: 0,
                  max_orders_per_slot: null
                };

                return (
                  <Card key={key} className="bg-[#111C32] border-slate-700/50">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-xl bg-${color}-500/20 flex items-center justify-center`}>
                            <Icon className={`w-6 h-6 text-${color}-400`} />
                          </div>
                          <div>
                            <CardTitle className="text-white">{label}</CardTitle>
                            <CardDescription className="text-slate-400">
                              {rule.enabled ? 'Aktiviert' : 'Deaktiviert'}
                            </CardDescription>
                          </div>
                        </div>
                        <Switch
                          checked={rule.enabled}
                          onCheckedChange={(checked) => {
                            updateOrderTypeRule(key, { ...rule, enabled: checked });
                          }}
                          className="data-[state=checked]:bg-teal-500"
                          data-testid={`toggle-${key}`}
                        />
                      </div>
                    </CardHeader>
                    
                    {rule.enabled && (
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-slate-300">Mindestbestellwert (€)</Label>
                            <Input
                              type="number"
                              step="0.5"
                              value={rule.min_order_value || 0}
                              onChange={(e) => {
                                updateOrderTypeRule(key, { ...rule, min_order_value: parseFloat(e.target.value) });
                              }}
                              className="mt-1 bg-[#0E1628] border-slate-700 text-white"
                              data-testid={`min-value-${key}`}
                            />
                          </div>

                          <div>
                            <Label className="text-slate-300">Vorlaufzeit (Min.)</Label>
                            <Input
                              type="number"
                              value={rule.lead_time_minutes || 0}
                              onChange={(e) => {
                                updateOrderTypeRule(key, { ...rule, lead_time_minutes: parseInt(e.target.value) });
                              }}
                              className="mt-1 bg-[#0E1628] border-slate-700 text-white"
                              data-testid={`lead-time-${key}`}
                            />
                            <p className="text-xs text-slate-500 mt-1">
                              Bestellungen mind. X Min. im Voraus
                            </p>
                          </div>

                          {key !== 'dine_in' && (
                            <div>
                              <Label className="text-slate-300">Max. Bestellungen/Stunde</Label>
                              <Input
                                type="number"
                                value={rule.max_orders_per_slot || ''}
                                onChange={(e) => {
                                  updateOrderTypeRule(key, { 
                                    ...rule, 
                                    max_orders_per_slot: e.target.value ? parseInt(e.target.value) : null 
                                  });
                                }}
                                className="mt-1 bg-[#0E1628] border-slate-700 text-white"
                                placeholder="Unbegrenzt"
                                data-testid={`max-orders-${key}`}
                              />
                              <p className="text-xs text-slate-500 mt-1">
                                Verhindert Überlastung
                              </p>
                            </div>
                          )}

                          <div className="flex items-center justify-between p-4 bg-slate-800/30 rounded-lg">
                            <Label className="text-slate-300">Automatisch annehmen</Label>
                            <Switch
                              checked={rule.auto_accept !== false}
                              onCheckedChange={(checked) => {
                                updateOrderTypeRule(key, { ...rule, auto_accept: checked });
                              }}
                              className="data-[state=checked]:bg-emerald-500"
                              data-testid={`auto-accept-${key}`}
                            />
                          </div>
                        </div>
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* TAB 2: LIEFERGEBIETE */}
          <TabsContent value="delivery-zones">
            <div className="space-y-6">
              
              {/* Neues Liefergebiet */}
              <Card className="bg-[#111C32] border-slate-700/50">
                <CardHeader>
                  <CardTitle className="text-white">Neues Liefergebiet</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-slate-300">Name</Label>
                      <Input
                        value={newZone.name}
                        onChange={(e) => setNewZone({...newZone, name: e.target.value})}
                        className="mt-1 bg-[#0E1628] border-slate-700 text-white"
                        placeholder="Innenstadt"
                        data-testid="new-zone-name"
                      />
                    </div>

                    <div>
                      <Label className="text-slate-300">Postleitzahlen (kommagetrennt)</Label>
                      <Input
                        value={newZone.postal_codes}
                        onChange={(e) => setNewZone({...newZone, postal_codes: e.target.value})}
                        className="mt-1 bg-[#0E1628] border-slate-700 text-white"
                        placeholder="10115, 10117, 10119"
                        data-testid="new-zone-plz"
                      />
                    </div>

                    <div>
                      <Label className="text-slate-300">Mindestbestellwert (€)</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={newZone.min_order_value}
                        onChange={(e) => setNewZone({...newZone, min_order_value: parseFloat(e.target.value)})}
                        className="mt-1 bg-[#0E1628] border-slate-700 text-white"
                        data-testid="new-zone-min-value"
                      />
                    </div>

                    <div>
                      <Label className="text-slate-300">Liefergebühr (€)</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={newZone.delivery_fee}
                        onChange={(e) => setNewZone({...newZone, delivery_fee: parseFloat(e.target.value)})}
                        className="mt-1 bg-[#0E1628] border-slate-700 text-white"
                        data-testid="new-zone-fee"
                      />
                    </div>

                    <div>
                      <Label className="text-slate-300">Kostenlos ab (€)</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={newZone.delivery_fee_free_above}
                        onChange={(e) => setNewZone({...newZone, delivery_fee_free_above: parseFloat(e.target.value)})}
                        className="mt-1 bg-[#0E1628] border-slate-700 text-white"
                        data-testid="new-zone-free-above"
                      />
                    </div>

                    <div>
                      <Label className="text-slate-300">Lieferzeit (Min.)</Label>
                      <div className="flex gap-2 mt-1">
                        <Input
                          type="number"
                          value={newZone.estimated_time_min}
                          onChange={(e) => setNewZone({...newZone, estimated_time_min: parseInt(e.target.value)})}
                          className="bg-[#0E1628] border-slate-700 text-white"
                          placeholder="30"
                          data-testid="new-zone-time-min"
                        />
                        <span className="text-slate-400 flex items-center">-</span>
                        <Input
                          type="number"
                          value={newZone.estimated_time_max}
                          onChange={(e) => setNewZone({...newZone, estimated_time_max: parseInt(e.target.value)})}
                          className="bg-[#0E1628] border-slate-700 text-white"
                          placeholder="45"
                          data-testid="new-zone-time-max"
                        />
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={createDeliveryZone}
                    disabled={!newZone.name || !newZone.postal_codes}
                    className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-white h-12"
                    data-testid="create-zone-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Liefergebiet hinzufügen
                  </Button>
                </CardContent>
              </Card>

              {/* Bestehende Liefergebiete */}
              <div className="grid grid-cols-1 gap-4">
                {deliveryZones.map((zone) => (
                  <Card key={zone.zone_id} className="bg-[#111C32] border-slate-700/50">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                            <MapPin className="w-6 h-6 text-emerald-400" />
                          </div>
                          <div>
                            <CardTitle className="text-white">{zone.name}</CardTitle>
                            <CardDescription className="text-slate-400">
                              PLZ: {zone.postal_codes?.join(', ')}
                            </CardDescription>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteDeliveryZone(zone.zone_id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          data-testid={`delete-zone-${zone.zone_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-4 gap-4">
                        <div className="p-3 bg-slate-800/30 rounded-lg">
                          <div className="text-xs text-slate-400 mb-1">Mindestbestellwert</div>
                          <div className="text-lg font-bold text-white">{zone.min_order_value}€</div>
                        </div>
                        <div className="p-3 bg-slate-800/30 rounded-lg">
                          <div className="text-xs text-slate-400 mb-1">Liefergebühr</div>
                          <div className="text-lg font-bold text-white">{zone.delivery_fee}€</div>
                        </div>
                        <div className="p-3 bg-slate-800/30 rounded-lg">
                          <div className="text-xs text-slate-400 mb-1">Kostenlos ab</div>
                          <div className="text-lg font-bold text-white">{zone.delivery_fee_free_above}€</div>
                        </div>
                        <div className="p-3 bg-slate-800/30 rounded-lg">
                          <div className="text-xs text-slate-400 mb-1">Lieferzeit</div>
                          <div className="text-lg font-bold text-white">
                            {zone.estimated_time_min}-{zone.estimated_time_max} Min
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {deliveryZones.length === 0 && (
                  <Card className="bg-[#111C32]/50 border-slate-700/30">
                    <CardContent className="p-12 text-center">
                      <MapPin className="w-16 h-16 mx-auto mb-4 text-slate-600" />
                      <p className="text-slate-400 mb-2">Noch keine Liefergebiete konfiguriert</p>
                      <p className="text-slate-500 text-sm">Erstellen Sie oben Ihr erstes Liefergebiet</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* TAB 3: PRODUKTVERFÜGBARKEIT */}
          <TabsContent value="availability">
            <Card className="bg-[#111C32] border-slate-700/50">
              <CardHeader>
                <CardTitle className="text-white">Zeitbasierte Produktverfügbarkeit</CardTitle>
                <CardDescription className="text-slate-400">
                  z.B. Frühstück nur 08:00-11:00, Pizza ab 17:00, Spargel Apr-Jun
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                
                {availabilityRules.length === 0 ? (
                  <div className="p-12 bg-slate-800/30 rounded-lg border border-slate-700/50 text-center">
                    <Clock className="w-16 h-16 mx-auto mb-4 text-slate-600" />
                    <p className="text-slate-400 mb-2">Noch keine Zeitregeln konfiguriert</p>
                    <p className="text-slate-500 text-sm mb-4">
                      Erstellen Sie Regeln direkt beim Produkt (Stammdaten)
                    </p>
                    <Button
                      onClick={() => window.location.href = '/app/stammdaten'}
                      className="bg-teal-500 hover:bg-teal-600"
                    >
                      Zu Stammdaten
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {availabilityRules.map((rule) => (
                      <div key={rule.rule_id} className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-white font-semibold mb-1">
                              Produkt: {rule.product_id}
                            </div>
                            <div className="text-sm text-slate-400">
                              {rule.availability_type === 'time_of_day' && `${rule.time_start} - ${rule.time_end}`}
                              {rule.availability_type === 'day_of_week' && `Nur bestimmte Wochentage`}
                              {rule.availability_type === 'seasonal' && `${rule.season_start} - ${rule.season_end}`}
                            </div>
                          </div>
                          <Badge className={rule.active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'}>
                            {rule.active ? 'Aktiv' : 'Inaktiv'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 4: ERWEITERT */}
          <TabsContent value="advanced">
            <div className="grid grid-cols-1 gap-6">
              
              {/* Notfall-Pause */}
              <Card className="bg-[#111C32] border-slate-700/50">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <ShieldAlert className="w-5 h-5 text-amber-400" />
                    Notfall-Pause
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Alle Bestellungen sofort pausieren (z.B. bei Überlastung, Notfall)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <div>
                      <div className="text-white font-semibold">Bestellannahme pausieren</div>
                      <div className="text-sm text-amber-400">Kunden sehen: "Derzeit keine Bestellungen möglich"</div>
                    </div>
                    <Switch
                      onCheckedChange={(checked) => pauseAllOrders(checked)}
                      className="data-[state=checked]:bg-amber-500"
                      data-testid="pause-all-orders"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Kapazitäts-Management */}
              <Card className="bg-[#111C32] border-slate-700/50">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-teal-400" />
                    Kapazitäts-Management
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Automatische Bestelllimits basierend auf Küchen-Auslastung
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-6 bg-gradient-to-br from-teal-500/10 to-emerald-500/10 rounded-xl border border-teal-500/30">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-teal-500/20 flex items-center justify-center flex-shrink-0">
                        <Timer className="w-6 h-6 text-teal-400" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold mb-2">Intelligente Auslastung</h3>
                        <p className="text-sm text-slate-300 mb-3">
                          System berechnet automatisch ob Küche Bestellung in gewünschter Zeit schafft
                        </p>
                        <Badge variant="outline" className="text-emerald-400 border-emerald-400/50 bg-emerald-400/10">
                          ✅ Backend implementiert
                        </Badge>
                        <Badge variant="outline" className="ml-2 text-amber-400 border-amber-400/50 bg-amber-400/10">
                          Weitere Konfiguration in v2
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Blacklist-Info */}
              <Card className="bg-[#111C32] border-slate-700/50">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <ShieldAlert className="w-5 h-5 text-red-400" />
                    Kunden-Blacklist
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Kunden/IPs bei Missbrauch automatisch sperren
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-6 bg-slate-800/30 rounded-lg border border-slate-700/50">
                    <h3 className="text-white font-semibold mb-3">Automatische Sperre bei:</h3>
                    <div className="space-y-2 text-sm text-slate-300">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-red-400" />
                        <span>Mehrfache Stornierungen ({'>'}3 in 24h)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-red-400" />
                        <span>Spam-Bestellungen von gleicher IP</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-red-400" />
                        <span>Ungültige Zahlungen</span>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-3">
                      Manuelle Verwaltung in v2
                    </p>
                  </div>
                </CardContent>
              </Card>

            </div>
          </TabsContent>

        </Tabs>
      </div>
    </div>
  );
}
